import { MonarchSlot, useMonarchSlot } from '@redventures/cohesion-utils-react';
import { isEqual } from 'lodash';
import dynamic from 'next/dynamic';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { PAGE_ROUTES } from '~/constants/pages';
import { FuelContext } from '~/providers/FuelProvider';
import { CartConfigEditableLoader, Loader as LoaderData } from '~/types/editable-loader';
import { ReduxState } from '~/types/redux';
import {
  cartConfigEditableLoaderDefaultValue,
  UNSERVICEABLE_PROGRESS_STEPS,
} from '../../../monarch/Config | Editable Loader/default';

const Default = dynamic(() => import('./components/Default'));
const AddressCheck = dynamic(() => import('./components/AddressCheck'));
const CreditCheck = dynamic(() => import('./components/CreditCheck'));
const Checkout = dynamic(() => import('./components/Checkout'));
const EditableLoader = dynamic(() => import('./components/EditableLoader'));

const Loader = (): JSX.Element => {
  const {
    err,
    result,
    data: metadata,
  } = useMonarchSlot<CartConfigEditableLoader>('cartConfigEditableLoader', {
    default: cartConfigEditableLoaderDefaultValue,
  });

  // Detect if fallback value is being used
  const isUsingMonarchFallback = !!err || !result || isEqual(metadata, cartConfigEditableLoaderDefaultValue);

  const { router } = useContext(FuelContext);
  const { pathname: currentPage } = router;
  // Monarch does not allow slashes in custom Ids so we replace them with underscores
  const formattedCurrentPage = currentPage.replace('/', '_');
  const { pageLoading, unservRedirect } = useSelector((state: ReduxState) => state);

  const addressCheck = formattedCurrentPage === PAGE_ROUTES.ADDRESS_CHECK.replace('/', '_');
  const creditCheckPage = formattedCurrentPage === PAGE_ROUTES.CONTACT_CREDIT.replace('/', '_');
  const productSelectionStep = formattedCurrentPage === PAGE_ROUTES.PLAN_PACKAGE.replace('/', '_');
  const fiber = formattedCurrentPage === PAGE_ROUTES.BUY_FIBER.replace('/', '_');
  const resume = formattedCurrentPage === PAGE_ROUTES.RESUME.replace('/', '_');
  const checkout = formattedCurrentPage === PAGE_ROUTES.CHECKOUT.replace('/', '_');

  const loadingText = (() => {
    if (productSelectionStep) return 'Just a moment, we’re getting your plan ready for activation.';
    if (creditCheckPage) return 'This step may take up to 2 minutes.';
    if (fiber) return 'Searching for the best fiber upgrades.';
    if (resume) return 'Loading your previous selections.';
    return 'Just a moment...';
  })();
  const loadingTextHeader = creditCheckPage ? 'Verifying your identity' : '';

  // Move this check outside of the Preamp placement so we don't get
  // a flash of the confirmation page before redirecting to Frontier's page
  if (currentPage.includes('confirmation')) {
    return <Default currentPage={currentPage} loadingText={loadingText} loadingTextHeader={loadingTextHeader} />;
  }

  const disableLoader = !pageLoading;

  if (disableLoader) {
    return null;
  }

  const currentPageConfig = metadata?.[formattedCurrentPage] as CartConfigEditableLoader;
  const loaderData = (currentPageConfig || {}) as LoaderData;

  // Locally override progressSteps with unserviceableProgressSteps for unserviceable addresses
  if (isUsingMonarchFallback && unservRedirect?.provider) {
    metadata.progressSteps = UNSERVICEABLE_PROGRESS_STEPS;
  }

  if (unservRedirect?.provider || (currentPageConfig?.enabled && currentPage !== '/')) {
    return <EditableLoader metadata={metadata} loaderData={unservRedirect?.provider ? metadata?._ : loaderData} />;
  }

  if (addressCheck || fiber) {
    return <AddressCheck metadata={metadata} />;
  }

  if (creditCheckPage) {
    return <CreditCheck />;
  }

  if (checkout) {
    return <Checkout />;
  }

  return (
    <MonarchSlot slotId="cartConfigEditableLoader">
      <Default currentPage={formattedCurrentPage} loadingText={loadingText} loadingTextHeader={loadingTextHeader} />
    </MonarchSlot>
  );
};

Loader.displayName = 'Loader';

export default Loader;
